import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  TableEmpty,
} from '@/components/ui/Table';
import { CheckIcon, GlobeIcon, SettingsIcon, TrashIcon } from '@/components/icons';
import { StatusIndicator } from '@/components/StatusIndicator';
import { domainStatusMap } from '@/config/statusMaps';
import type { Domain } from '@/types/domain';

/**
 * Row actions of the domains table
 */
export type DomainRowAction = 'validateDns' | 'edit' | 'delete';

export interface DomainsTableProps {
  domains: Domain[];
  isLoading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  /** Shown in the empty state when set (user may create domains) */
  onAddFirst?: () => void;
  /** Comma-separated names of the nodes serving a domain */
  getNodeNames: (nodeIds: string[]) => string;
  getActions: (domain: Domain) => Record<DomainRowAction, boolean>;
  isBusy: (action: DomainRowAction, domainId: string) => boolean;
  onAction: (action: DomainRowAction, domain: Domain) => void;
}

const COLUMN_COUNT = 5;

/**
 * DNS validation state shown in the SSL column
 */
function SSLIndicator({ dnsValidated }: { dnsValidated: boolean }) {
  if (dnsValidated) {
    return (
      <span className="domains-ssl domains-ssl-valid">
        <span className="domains-ssl-icon">
          <CheckIcon />
        </span>
        Valid
      </span>
    );
  }

  return (
    <span className="domains-ssl domains-ssl-pending">
      <span className="domains-ssl-icon">...</span>
      Pending
    </span>
  );
}

/**
 * Domains table
 */
export function DomainsTable({
  domains,
  isLoading,
  hasActiveFilters,
  onClearFilters,
  onAddFirst,
  getNodeNames,
  getActions,
  isBusy,
  onAction,
}: DomainsTableProps) {
  return (
    <Table isLoading={isLoading} skeletonColumns={COLUMN_COUNT} skeletonRows={5}>
      <TableHead>
        <TableRow>
          <TableHeader>Domain</TableHeader>
          <TableHeader>Nodes</TableHeader>
          <TableHeader>SSL</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader align="right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {domains.length === 0 && !isLoading ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <TableEmpty
                icon={<GlobeIcon />}
                message={hasActiveFilters ? 'No domains match your filters' : 'No domains configured yet'}
                action={
                  hasActiveFilters ? (
                    <Button variant="secondary" size="sm" onClick={onClearFilters}>
                      Clear filters
                    </Button>
                  ) : (
                    onAddFirst && (
                      <Button variant="secondary" size="sm" onClick={onAddFirst}>
                        Add your first domain
                      </Button>
                    )
                  )
                }
              />
            </TableCell>
          </TableRow>
        ) : (
          domains.map((domain) => {
            const actions = getActions(domain);

            return (
              <TableRow key={domain.id}>
                <TableCell>
                  <div className="domains-name-cell">
                    <span className="domains-name">{domain.name}</span>
                    <span className="domains-origin">
                      {domain.origin_url || domain.origin_group?.name}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="domains-node">{getNodeNames(domain.node_ids)}</span>
                </TableCell>
                <TableCell>
                  <SSLIndicator dnsValidated={domain.dns_validated} />
                </TableCell>
                <TableCell>
                  <StatusIndicator status={domain.status} map={domainStatusMap} baseClassName="domains-status" />
                </TableCell>
                <TableCell align="right">
                  <div className="domains-actions">
                    {actions.validateDns && (
                      <IconButton
                        className="domains-action-btn"
                        icon={<CheckIcon />}
                        label={`Validate DNS for ${domain.name}`}
                        title="Validate DNS"
                        isBusy={isBusy('validateDns', domain.id)}
                        onClick={() => onAction('validateDns', domain)}
                      />
                    )}
                    {actions.edit && (
                      <IconButton
                        className="domains-action-btn"
                        icon={<SettingsIcon />}
                        label={`Edit ${domain.name}`}
                        title="Edit domain"
                        onClick={() => onAction('edit', domain)}
                      />
                    )}
                    {actions.delete && (
                      <IconButton
                        className="domains-action-btn domains-action-btn-danger"
                        icon={<TrashIcon />}
                        label={`Delete ${domain.name}`}
                        title="Delete domain"
                        onClick={() => onAction('delete', domain)}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
